import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { surveyApi, usersApi } from '@/lib/apiClient';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import { fmtDate as fmtDateOnly } from '@/lib/dates';
import { SurveyStatusBadge } from './statusBadge';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserRef    { id: string; displayName: string | null }
interface ProjectRef { id: number; jobNumber: string; title: string; primaryPm: UserRef | null }

interface SurveyTaskType { id: number; code: string; label: string }

interface SurveyTask {
  id: number;
  taskTypeId: number;
  planned: boolean;
  detailNotes: string | null;
  taskType: SurveyTaskType;
}

interface SurveyLogTask {
  id: number;
  taskTypeId: number;
  completed: boolean;
  detailNotes: string | null;
  taskType: SurveyTaskType;
}

interface SurveyLog {
  id: number;
  surveyRequestId: number | null;
  jobNumber: string | null;
  jobName: string | null;
  logDate: string | null;
  loggedById: string | null;
  loggedByText: string | null;
  notes: string | null;
  photoFolderUrl: string | null;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  loggedBy: UserRef | null;
  tasks: SurveyLogTask[];
}

interface SurveyRequest {
  id: number;
  projectId: number | null;
  title: string | null;
  jobName: string | null;
  requestedByText: string | null;
  requestedAt: string | null;
  siteVisitDateTime: string | null;
  duration: string | null;
  durationDetails: string | null;
  plansetRef: string | null;
  siteReady: string | null;
  concretePour: string | null;
  pourDateTime: string | null;
  anchorBoltCheck: string | null;
  notes: string | null;
  scheduledDateTime: string | null;
  scheduledTechnicianText: string | null;
  surveyNotes: string | null;
  formstackSubmissionId: string | null;
  fieldLeaderEmail: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  project: ProjectRef | null;
  requestedBy: UserRef | null;
  tasks: SurveyTask[];
  logs: SurveyLog[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = ['Submitted', 'Scheduled', 'In Progress', 'Complete', 'Canceled'] as const;

// STATUS_COLORS + StatusBadge moved to ./statusBadge (shared with the project Survey tab).

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Timezone-aware date formatter for timestamp fields displayed without a
// time component (e.g. "Requested At"). Use fmtDateOnly from @/lib/dates
// for true date-only fields like logDate.
function fmtDate(val: string | null | undefined) {
  if (!val) return '—';
  const d = new Date(val);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateTime(val: string | null | undefined) {
  if (!val) return '—';
  const d = new Date(val);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

const StatusBadge = SurveyStatusBadge;

// ─── Detail bits ──────────────────────────────────────────────────────────────

/**
 * Can the signed-in user perform survey writes?
 *
 * This module rendered every action button to everyone and let the API 403 on
 * save, which reads as a broken feature rather than a missing permission.
 *
 * Note the deliberate asymmetry on the API, preserved rather than flattened:
 * CREATING a request accepts `survey.edit` OR `projects.edit`, but advancing
 * one (schedule / start / complete / cancel) and every visit-log write require
 * `survey.edit` alone. So a PM holding only `projects.edit` can submit a
 * request but not advance it. Request intake here is a Formstack link rather
 * than an API call, so this page only ever needs the stricter `survey.edit`.
 *
 * Admin bypasses the permissions guard server-side, so it is an explicit
 * escape hatch here too. Safe to call from several components — react-query
 * dedupes on the shared 'me' key.
 */
function useCanEditSurvey(): boolean {
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => usersApi.me(),
    staleTime: 5 * 60 * 1000,
  });
  return (me?.roles.includes('Admin') ?? false)
    || (me?.permissions.includes('survey.edit') ?? false);
}

function DF({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 12, padding: '3px 0', borderBottom: '1px solid #f3f4f6', gap: 12 }}>
      <span style={{ color: '#6b7280', flexShrink: 0 }}>{label}</span>
      <span style={{ fontWeight: 500, textAlign: 'right', color: value ? '#1f2937' : '#9ca3af', minWidth: 0, overflowWrap: 'anywhere' }}>{value ?? '—'}</span>
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-lg)', padding: '14px 16px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px', color: '#6b7280', marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}

// ─── Status Actions ───────────────────────────────────────────────────────────

type ActionMode = 'idle' | 'scheduling' | 'completing' | 'canceling';

function StatusActions({ request }: { request: SurveyRequest }) {
  const queryClient = useQueryClient();
  const canEdit = useCanEditSurvey();
  const [mode, setMode] = useState<ActionMode>('idle');
  const [scheduledDate, setScheduledDate] = useState<string>('');
  const [technicianDraft, setTechnicianDraft] = useState<string>('');
  const [noteDraft, setNoteDraft] = useState<string>('');

  const mutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => surveyApi.updateRequest(request.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-request', request.id] });
      queryClient.invalidateQueries({ queryKey: ['survey-requests'] });
      setMode('idle');
      setNoteDraft('');
      setScheduledDate('');
      setTechnicianDraft('');
    },
  });

  const isTerminal = request.status === 'Complete' || request.status === 'Canceled';

  const btnPrimary: React.CSSProperties = { fontSize: 12, padding: '5px 14px', background: 'var(--ew)', color: '#fff', border: 'none', borderRadius: 'var(--ew-r-md)', cursor: 'pointer', fontWeight: 600 };
  const btnSecondary: React.CSSProperties = { fontSize: 12, padding: '5px 12px', background: '#fff', color: 'var(--gray)', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', cursor: 'pointer', fontWeight: 500 };
  const btnDanger: React.CSSProperties = { fontSize: 12, padding: '5px 12px', background: '#fff', color: '#8F2414', border: '1px solid #f3c8c0', borderRadius: 'var(--ew-r-md)', cursor: 'pointer', fontWeight: 500 };
  const inp: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', padding: '6px 10px', fontSize: 13, fontFamily: 'inherit', background: '#fff', color: 'var(--text)' };

  const goSchedule = () => {
    if (!scheduledDate) return;
    // <input type="datetime-local"> returns a string like "2026-05-18T07:30"
    // with no timezone. The browser interprets that as local wall-clock; we
    // convert to a UTC ISO string here so the server stores the correct
    // absolute moment regardless of where the API host runs.
    const iso = new Date(scheduledDate).toISOString();
    const tech = technicianDraft.trim();
    mutation.mutate({
      status: 'Scheduled',
      scheduledDateTime: iso,
      scheduledTechnicianText: tech.length ? tech : null,
    });
  };
  const goStart = () => mutation.mutate({ status: 'In Progress' });
  const goComplete = () => {
    const payload: Record<string, unknown> = { status: 'Complete' };
    if (noteDraft.trim()) payload.appendNote = noteDraft.trim();
    mutation.mutate(payload);
  };
  const goCancel = () => {
    const payload: Record<string, unknown> = { status: 'Canceled' };
    if (noteDraft.trim()) payload.appendNote = noteDraft.trim();
    mutation.mutate(payload);
  };

  // Pre-fill the schedule input with the existing scheduled date if one is set,
  // converting the stored UTC ISO to a local datetime-local string so the user
  // sees the time they originally picked in their own timezone.
  const startScheduling = () => {
    if (request.scheduledDateTime) {
      const d = new Date(request.scheduledDateTime);
      if (!Number.isNaN(d.getTime())) {
        const pad = (n: number) => String(n).padStart(2, '0');
        setScheduledDate(
          `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`,
        );
      } else {
        setScheduledDate('');
      }
    } else {
      setScheduledDate('');
    }
    setTechnicianDraft(request.scheduledTechnicianText ?? '');
    setMode('scheduling');
  };

  return (
    <DetailSection title="Status">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: '#6b7280' }}>Current:</span>
          <StatusBadge label={request.status} />
          {isTerminal && (
            <span style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>
              Terminal — no further actions.
            </span>
          )}
        </div>

        {mode === 'idle' && !isTerminal && canEdit && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {request.status === 'Submitted' && (
              <button style={btnPrimary} onClick={startScheduling} disabled={mutation.isPending}>Schedule…</button>
            )}
            {request.status === 'Scheduled' && (
              <button style={btnPrimary} onClick={goStart} disabled={mutation.isPending}>Start work</button>
            )}
            {request.status === 'In Progress' && (
              <button style={btnPrimary} onClick={() => setMode('completing')} disabled={mutation.isPending}>Mark complete…</button>
            )}
            {request.status === 'Scheduled' && (
              <button style={btnSecondary} onClick={startScheduling} disabled={mutation.isPending}>Reschedule…</button>
            )}
            <button style={btnDanger} onClick={() => setMode('canceling')} disabled={mutation.isPending}>Cancel request…</button>
          </div>
        )}

        {mode === 'scheduling' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 4 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ fontSize: 12, color: '#6b7280' }}>Scheduled date/time</label>
              <input type="datetime-local" style={inp} value={scheduledDate} onChange={e => setScheduledDate(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ fontSize: 12, color: '#6b7280' }}>Survey technician</label>
              <input
                type="text"
                style={{ ...inp, minWidth: 220 }}
                value={technicianDraft}
                onChange={e => setTechnicianDraft(e.target.value)}
                placeholder="Who's going (optional)"
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={btnPrimary} onClick={goSchedule} disabled={!scheduledDate || mutation.isPending}>
                {mutation.isPending ? 'Saving…' : 'Save'}
              </button>
              <button
                style={btnSecondary}
                onClick={() => { setMode('idle'); setScheduledDate(''); setTechnicianDraft(''); }}
                disabled={mutation.isPending}
              >
                Discard
              </button>
            </div>
          </div>
        )}

        {mode === 'completing' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 4 }}>
            <label style={{ fontSize: 12, color: '#6b7280' }}>Completion note (optional — appended to Survey notes)</label>
            <textarea style={{ ...inp, resize: 'vertical', minHeight: 60 }} value={noteDraft} onChange={e => setNoteDraft(e.target.value)}
              placeholder="What was done, any leftover items, photos uploaded, etc." />
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={btnPrimary} onClick={goComplete} disabled={mutation.isPending}>
                {mutation.isPending ? 'Saving…' : 'Mark complete'}
              </button>
              <button style={btnSecondary} onClick={() => { setMode('idle'); setNoteDraft(''); }} disabled={mutation.isPending}>Discard</button>
            </div>
          </div>
        )}

        {mode === 'canceling' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 4 }}>
            <label style={{ fontSize: 12, color: '#6b7280' }}>Reason for cancel (optional — appended to Survey notes)</label>
            <textarea style={{ ...inp, resize: 'vertical', minHeight: 60 }} value={noteDraft} onChange={e => setNoteDraft(e.target.value)}
              placeholder="Why is this being canceled?" />
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={btnDanger} onClick={goCancel} disabled={mutation.isPending}>
                {mutation.isPending ? 'Saving…' : 'Confirm cancel'}
              </button>
              <button style={btnSecondary} onClick={() => { setMode('idle'); setNoteDraft(''); }} disabled={mutation.isPending}>Discard</button>
            </div>
          </div>
        )}

        {mutation.isError && (
          <div style={{ fontSize: 12, color: '#8F2414', background: '#FBE4DF', padding: 8, borderRadius: 'var(--ew-r-md)' }}>
            {(mutation.error as Error).message}
          </div>
        )}
      </div>
    </DetailSection>
  );
}

// ─── Detail Modal ─────────────────────────────────────────────────────────────

function RequestDetailModal({ requestId, onClose }: { requestId: number; onClose: () => void }) {
  const canEdit = useCanEditSurvey();
  const { data, isLoading, error } = useQuery<SurveyRequest>({
    queryKey: ['survey-request', requestId],
    queryFn: () => surveyApi.getRequest(requestId),
  });

  const [showAddLog, setShowAddLog] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, { onEscape: onClose });

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 100,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 16px',
      }}
    >
      <div
        ref={modalRef}
        onClick={e => e.stopPropagation()}
        style={{
          background: '#f9fafb', borderRadius: 'var(--ew-r-lg)', width: 'min(960px, 100%)', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', background: '#fff', borderRadius: 'var(--ew-r-lg) var(--ew-r-lg) 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>
              {data ? `#${data.project?.jobNumber ?? '—'} · ${data.jobName ?? data.title ?? 'Survey Request'}` : 'Survey Request'}
            </div>
            {data && <StatusBadge label={data.status} />}
          </div>
          <button onClick={onClose}
            style={{ fontSize: 12, padding: '5px 12px', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', background: '#fff', cursor: 'pointer', color: '#6b7280' }}>
            Close
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px', overflowY: 'auto' }}>
          {isLoading && <div style={{ padding: 32, textAlign: 'center', color: 'var(--gray)' }}>Loading…</div>}
          {error && <div style={{ padding: 12, background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 13 }}>{(error as Error).message}</div>}
          {data && (
            <>
              <div style={{ marginBottom: 12 }}>
                <StatusActions request={data} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12, marginBottom: 12 }}>
                <DetailSection title="Project & Request">
                  <DF label="Project" value={data.project ? `#${data.project.jobNumber} — ${data.project.title}` : '—'} />
                  <DF label="Project Manager" value={data.project?.primaryPm?.displayName ?? '—'} />
                  <DF label="Requested By" value={data.requestedByText ?? data.requestedBy?.displayName ?? '—'} />
                  <DF label="Requested At" value={fmtDate(data.requestedAt)} />
                  <DF label="Field Leader Email" value={data.fieldLeaderEmail ?? '—'} />
                  <DF label="Planset Ref" value={data.plansetRef ?? '—'} />
                </DetailSection>

                <DetailSection title="Site Visit">
                  <DF label="Site Visit Date/Time" value={fmtDateTime(data.siteVisitDateTime)} />
                  <DF label="Scheduled Date/Time" value={fmtDateTime(data.scheduledDateTime)} />
                  <DF label="Survey Technician" value={data.scheduledTechnicianText ?? '—'} />
                  <DF label="Duration" value={data.duration ?? '—'} />
                  <DF label="Duration Details" value={data.durationDetails ?? '—'} />
                  <DF label="Site Ready?" value={data.siteReady ?? '—'} />
                </DetailSection>

                <DetailSection title="Concrete">
                  <DF label="Concrete Pour?" value={data.concretePour ?? '—'} />
                  <DF label="Pour Date/Time" value={fmtDateTime(data.pourDateTime)} />
                  <DF label="Anchor Bolt Check?" value={data.anchorBoltCheck ?? '—'} />
                </DetailSection>
              </div>

              <DetailSection title={`Scope of Work (${data.tasks.length})`}>
                {data.tasks.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>No SOW boxes checked.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: '#6b7280' }}>
                        <th style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', fontWeight: 600, width: '30%' }}>Task</th>
                        <th style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', fontWeight: 600 }}>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.tasks.map(t => (
                        <tr key={t.id}>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', fontWeight: 600 }}>{t.taskType.label}</td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', color: t.detailNotes ? '#1f2937' : '#9ca3af', whiteSpace: 'pre-wrap' }}>
                            {t.detailNotes ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </DetailSection>

              {(data.notes || data.surveyNotes) && (
                <div style={{ marginTop: 12 }}>
                  <DetailSection title="Notes">
                    {data.notes && (
                      <div style={{ marginBottom: data.surveyNotes ? 10 : 0 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.6px', color: '#9ca3af', marginBottom: 4 }}>From requester</div>
                        <p style={{ fontSize: 12, margin: 0, whiteSpace: 'pre-wrap', color: '#1f2937' }}>{data.notes}</p>
                      </div>
                    )}
                    {data.surveyNotes && (
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.6px', color: '#9ca3af', marginBottom: 4 }}>Survey crew</div>
                        <p style={{ fontSize: 12, margin: 0, whiteSpace: 'pre-wrap', color: '#1f2937' }}>{data.surveyNotes}</p>
                      </div>
                    )}
                  </DetailSection>
                </div>
              )}

              <div style={{ marginTop: 12 }}>
                <DetailSection title={`Visit Logs (${data.logs.length})`}>
                  {canEdit && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                      <button
                        onClick={() => setShowAddLog(true)}
                        style={{ fontSize: 12, padding: '5px 14px', background: 'var(--ew)', color: '#fff', border: 'none', borderRadius: 'var(--ew-r-md)', cursor: 'pointer', fontWeight: 600 }}>
                        + Add log
                      </button>
                    </div>
                  )}
                  {data.logs.length === 0 ? (
                    <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>No visit logs yet.</div>
                  ) : (
                    data.logs.map(log => (
                      <div key={log.id} style={{ padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                        <div style={{ display: 'flex', gap: 8, fontSize: 12 }}>
                          <span style={{ fontWeight: 600 }}>{fmtDateOnly(log.logDate)}</span>
                          <span style={{ color: '#6b7280' }}>{log.loggedBy?.displayName ?? log.loggedByText ?? '—'}</span>
                        </div>
                        {log.notes && <div style={{ fontSize: 12, marginTop: 4, whiteSpace: 'pre-wrap', color: '#1f2937' }}>{log.notes}</div>}
                      </div>
                    ))
                  )}
                </DetailSection>
              </div>

              <div style={{ marginTop: 12 }}>
                <DetailSection title="Meta">
                  <DF label="Formstack Submission ID" value={data.formstackSubmissionId ?? '—'} />
                  <DF label="Created" value={fmtDateTime(data.createdAt)} />
                  <DF label="Updated" value={fmtDateTime(data.updatedAt)} />
                </DetailSection>
              </div>
            </>
          )}
        </div>
      </div>

      {showAddLog && data && (
        <LogCreateModal
          onClose={() => setShowAddLog(false)}
          prefill={{
            surveyRequestId: data.id,
            jobNumber: data.project?.jobNumber ?? data.title ?? '',
            jobName: data.jobName,
          }}
        />
      )}
    </div>
  );
}

// ─── Shared field input style ─────────────────────────────────────────────────

const selectStyle: React.CSSProperties = {
  border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', padding: '6px 10px',
  fontSize: 13, fontFamily: 'inherit', background: '#fff', color: 'var(--text)',
};

// ─── Requests List ────────────────────────────────────────────────────────────

function RequestsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch]       = useState('');
  const [status, setStatus]       = useState<string>('');
  const [from, setFrom]           = useState<string>('');
  const [to, setTo]               = useState<string>('');
  const [selectedId, setSelectedId] = useState<number | null>(() => {
    // Deep-link: `/survey?request=149` auto-opens the request modal on mount so
    // links from other tabs (e.g. the Manpower survey chip) land directly on
    // the relevant request.
    const param = searchParams.get('request');
    if (!param) return null;
    const id = Number(param);
    return Number.isFinite(id) && id > 0 ? id : null;
  });

  // Clear `?request=` from the URL when the modal closes so revisiting the tab
  // doesn't re-open the same request and the URL reflects the actual UI state.
  const handleCloseModal = () => {
    setSelectedId(null);
    if (searchParams.has('request')) {
      const next = new URLSearchParams(searchParams);
      next.delete('request');
      setSearchParams(next, { replace: true });
    }
  };

  const filters = useMemo(() => ({
    ...(search ? { job: search } : {}),
    ...(status ? { status } : {}),
    ...(from   ? { from }   : {}),
    ...(to     ? { to }     : {}),
  }), [search, status, from, to]);

  const { data: requests = [], isLoading, error } = useQuery<SurveyRequest[]>({
    queryKey: ['survey-requests', filters],
    queryFn: () => surveyApi.listRequests(filters),
  });

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: 'var(--gray)' }}>
          {isLoading ? 'Loading…' : `${requests.length} request${requests.length !== 1 ? 's' : ''}`}
        </span>
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 16, padding: '12px 16px', background: '#fff', borderRadius: 'var(--ew-r-lg)', border: '1px solid var(--border)' }}>
        <input
          type="search"
          placeholder="Search job number, project, or job name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...selectStyle, minWidth: 260, flexGrow: 1 }}
        />
        <select value={status} onChange={e => setStatus(e.target.value)} style={selectStyle}>
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <label style={{ fontSize: 12, color: 'var(--gray)' }}>
          From{' '}
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ ...selectStyle, padding: '5px 8px' }} />
        </label>
        <label style={{ fontSize: 12, color: 'var(--gray)' }}>
          To{' '}
          <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ ...selectStyle, padding: '5px 8px' }} />
        </label>
        {(search || status || from || to) && (
          <button
            onClick={() => { setSearch(''); setStatus(''); setFrom(''); setTo(''); }}
            style={{ fontSize: 12, padding: '5px 12px', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', background: '#fff', cursor: 'pointer', color: '#6b7280' }}
          >
            Clear
          </button>
        )}
      </div>

      {error && (
        <div style={{ padding: 12, background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', marginBottom: 16, fontSize: 13 }}>
          {(error as Error).message}
        </div>
      )}

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 'var(--ew-r-lg)', border: '1px solid var(--border)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '10%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '8%' }} />
          </colgroup>
          <thead>
            <tr>
              {['Job #', 'Job Name', 'Project Manager', 'Requested By', 'Site Visit', 'Duration', 'Status', 'Site Ready?'].map(h => (
                <th key={h} style={{
                  padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 12,
                  color: 'var(--gray)', borderBottom: '2px solid var(--border)', background: 'var(--surface)',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: 'var(--gray)' }}>Loading…</td></tr>
            )}
            {!isLoading && requests.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: 'var(--gray)' }}>No survey requests match your filters.</td></tr>
            )}
            {requests.map(r => (
              <tr
                key={r.id}
                onClick={() => setSelectedId(r.id)}
                style={{ cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <td style={{ padding: '10px', fontWeight: 600 }}>{r.project?.jobNumber ?? '—'}</td>
                <td style={{ padding: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.jobName ?? r.title ?? '—'}
                </td>
                <td style={{ padding: '10px', color: r.project?.primaryPm ? '#1f2937' : '#9ca3af' }}>
                  {r.project?.primaryPm?.displayName ?? '—'}
                </td>
                <td style={{ padding: '10px', color: (r.requestedByText || r.requestedBy) ? '#1f2937' : '#9ca3af' }}>
                  {r.requestedByText ?? r.requestedBy?.displayName ?? '—'}
                </td>
                <td style={{ padding: '10px', color: r.siteVisitDateTime ? '#1f2937' : '#9ca3af' }}>
                  {fmtDateTime(r.siteVisitDateTime)}
                </td>
                <td style={{ padding: '10px', color: r.duration ? '#1f2937' : '#9ca3af' }}>
                  {r.duration ?? '—'}
                </td>
                <td style={{ padding: '10px' }}><StatusBadge label={r.status} /></td>
                <td style={{ padding: '10px', color: r.siteReady ? '#1f2937' : '#9ca3af' }}>
                  {r.siteReady ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedId != null && (
        <RequestDetailModal requestId={selectedId} onClose={handleCloseModal} />
      )}
    </>
  );
}

// ─── Log Detail Modal ─────────────────────────────────────────────────────────

function LogDetailModal({
  logId, onClose, onEdit,
}: {
  logId: number;
  onClose: () => void;
  onEdit: (log: SurveyLog) => void;
}) {
  const canEdit = useCanEditSurvey();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery<SurveyLog>({
    queryKey: ['survey-log', logId],
    queryFn: () => surveyApi.getLog(logId),
  });

  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, { onEscape: onClose });

  const deleteMutation = useMutation({
    mutationFn: () => surveyApi.deleteLog(logId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-logs'] });
      if (data?.surveyRequestId != null) {
        queryClient.invalidateQueries({ queryKey: ['survey-request', data.surveyRequestId] });
        queryClient.invalidateQueries({ queryKey: ['survey-requests'] });
      }
      onClose();
    },
  });

  const handleDelete = () => {
    if (!data) return;
    const label = `${fmtDateOnly(data.logDate)} · ${data.jobNumber ?? '(no job #)'}${data.jobName ? ` — ${data.jobName}` : ''}`;
    if (window.confirm(`Delete this visit log?\n\n${label}\n\nThis will also remove its task entries. This cannot be undone.`)) {
      deleteMutation.mutate();
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 100,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 16px',
      }}
    >
      <div
        ref={modalRef}
        onClick={e => e.stopPropagation()}
        style={{
          background: '#f9fafb', borderRadius: 'var(--ew-r-lg)', width: 'min(960px, 100%)', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', background: '#fff', borderRadius: 'var(--ew-r-lg) var(--ew-r-lg) 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>
              {data
                ? `${fmtDateOnly(data.logDate)} · ${data.jobNumber ? `#${data.jobNumber}` : '(no job #)'}${data.jobName ? ` — ${data.jobName}` : ''}`
                : 'Survey Log'}
            </div>
            {data && data.surveyRequestId != null && (
              <span style={{ fontSize: 11, background: '#E1EEF7', color: '#0D4C7C', borderRadius: 'var(--ew-r-sm)', padding: '2px 10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Linked to request #{data.surveyRequestId}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {data && canEdit && (
              <>
                <button onClick={() => onEdit(data)} disabled={deleteMutation.isPending}
                  style={{ fontSize: 12, padding: '5px 14px', background: 'var(--ew)', color: '#fff', border: 'none', borderRadius: 'var(--ew-r-md)', cursor: 'pointer', fontWeight: 600 }}>
                  Edit
                </button>
                <button onClick={handleDelete} disabled={deleteMutation.isPending}
                  style={{ fontSize: 12, padding: '5px 12px', background: '#fff', color: '#8F2414', border: '1px solid #f3c8c0', borderRadius: 'var(--ew-r-md)', cursor: 'pointer', fontWeight: 500 }}>
                  {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
                </button>
              </>
            )}
            <button onClick={onClose}
              style={{ fontSize: 12, padding: '5px 12px', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', background: '#fff', cursor: 'pointer', color: '#6b7280' }}>
              Close
            </button>
          </div>
        </div>

        <div style={{ padding: '16px 20px', overflowY: 'auto' }}>
          {isLoading && <div style={{ padding: 32, textAlign: 'center', color: 'var(--gray)' }}>Loading…</div>}
          {error && <div style={{ padding: 12, background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 13 }}>{(error as Error).message}</div>}
          {deleteMutation.isError && (
            <div style={{ padding: 8, background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12, marginBottom: 12 }}>
              Delete failed: {(deleteMutation.error as Error).message}
            </div>
          )}
          {data && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12, marginBottom: 12 }}>
                <DetailSection title="Log Info">
                  <DF label="Log Date" value={fmtDateOnly(data.logDate)} />
                  <DF label="Job #" value={data.jobNumber ?? '—'} />
                  <DF label="Job Name" value={data.jobName ?? '—'} />
                  <DF label="Logged By" value={data.loggedBy?.displayName ?? data.loggedByText ?? '—'} />
                  <DF label="Linked Request" value={data.surveyRequestId != null ? `#${data.surveyRequestId}` : '—'} />
                  <DF label="Title" value={data.title ?? '—'} />
                </DetailSection>

                <DetailSection title="Photos">
                  {data.photoFolderUrl ? (
                    <a href={data.photoFolderUrl} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 12, color: 'var(--ew)', textDecoration: 'underline', overflowWrap: 'anywhere' }}>
                      {data.photoFolderUrl}
                    </a>
                  ) : (
                    <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>No photo folder linked.</div>
                  )}
                </DetailSection>
              </div>

              <DetailSection title={`Tasks Performed (${data.tasks.length})`}>
                {data.tasks.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>No tasks recorded.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: '#6b7280' }}>
                        <th style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', fontWeight: 600, width: '30%' }}>Task</th>
                        <th style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', fontWeight: 600, width: '8%' }}>Done?</th>
                        <th style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', fontWeight: 600 }}>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.tasks.map(t => (
                        <tr key={t.id}>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', fontWeight: 600 }}>{t.taskType.label}</td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', color: t.completed ? '#015A28' : '#9ca3af' }}>
                            {t.completed ? 'Yes' : 'No'}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', color: t.detailNotes ? '#1f2937' : '#9ca3af', whiteSpace: 'pre-wrap' }}>
                            {t.detailNotes ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </DetailSection>

              {data.notes && (
                <div style={{ marginTop: 12 }}>
                  <DetailSection title="Notes">
                    <p style={{ fontSize: 12, margin: 0, whiteSpace: 'pre-wrap', color: '#1f2937' }}>{data.notes}</p>
                  </DetailSection>
                </div>
              )}

              <div style={{ marginTop: 12 }}>
                <DetailSection title="Meta">
                  <DF label="Created" value={fmtDateTime(data.createdAt)} />
                  <DF label="Updated" value={fmtDateTime(data.updatedAt)} />
                </DetailSection>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Log Create Modal ─────────────────────────────────────────────────────────

interface TaskDraft { checked: boolean; detailNotes: string }

function todayYmd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

interface LogModalProps {
  onClose: () => void;
  /** When set, the modal renders in edit mode and PATCHes this log on save. */
  editLog?: SurveyLog;
  /** When set, the modal pre-fills + locks the job fields and links the log to a request. */
  prefill?: { surveyRequestId: number; jobNumber: string; jobName: string | null };
}

function LogCreateModal({ onClose, editLog, prefill }: LogModalProps) {
  const queryClient = useQueryClient();
  const isEdit = !!editLog;
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, { onEscape: onClose });

  // Form state — initialized from editLog or prefill if present.
  const [jobNumber, setJobNumber]         = useState(editLog?.jobNumber ?? prefill?.jobNumber ?? '');
  const [jobName, setJobName]             = useState(editLog?.jobName ?? prefill?.jobName ?? '');
  const [logDate, setLogDate]             = useState(
    editLog?.logDate ? editLog.logDate.slice(0, 10) : todayYmd(),
  );
  const [title, setTitle]                 = useState(editLog?.title ?? '');
  const [notes, setNotes]                 = useState(editLog?.notes ?? '');
  const [photoFolderUrl, setPhotoFolderUrl] = useState(editLog?.photoFolderUrl ?? '');
  const [taskDrafts, setTaskDrafts]       = useState<Record<number, TaskDraft>>(() => {
    if (!editLog) return {};
    const seed: Record<number, TaskDraft> = {};
    for (const t of editLog.tasks) {
      seed[t.taskTypeId] = { checked: t.completed, detailNotes: t.detailNotes ?? '' };
    }
    return seed;
  });

  const { data: taskTypes = [], isLoading: taskTypesLoading, error: taskTypesError } = useQuery<SurveyTaskType[]>({
    queryKey: ['survey-task-types'],
    queryFn: () => surveyApi.listTaskTypes(),
    staleTime: 10 * 60 * 1000,
  });

  const mutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      isEdit ? surveyApi.updateLog(editLog!.id, payload) : surveyApi.createLog(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-logs'] });
      if (isEdit) queryClient.invalidateQueries({ queryKey: ['survey-log', editLog!.id] });
      // If linked to a request, that request's cached detail (incl. its logs list) needs a refresh.
      const linkedRequestId = editLog?.surveyRequestId ?? prefill?.surveyRequestId;
      if (linkedRequestId != null) {
        queryClient.invalidateQueries({ queryKey: ['survey-request', linkedRequestId] });
        queryClient.invalidateQueries({ queryKey: ['survey-requests'] });
      }
      onClose();
    },
  });

  const setTask = (id: number, patch: Partial<TaskDraft>) => {
    setTaskDrafts(prev => {
      const existing = prev[id] ?? { checked: false, detailNotes: '' };
      return { ...prev, [id]: { ...existing, ...patch } };
    });
  };

  const handleSave = () => {
    if (!jobNumber.trim()) return;

    // For edit mode we send EVERY task type with its current checked state so the
    // server-side merge correctly toggles completed for tasks the user just
    // unchecked (the merge is upsert-only — it won't delete rows on its own).
    // For create mode we only send the checked ones.
    const tasks = isEdit
      ? taskTypes.map(tt => {
          const d = taskDrafts[tt.id] ?? { checked: false, detailNotes: '' };
          return {
            taskTypeId: tt.id,
            flag: d.checked,
            ...(d.detailNotes.trim() ? { detailNotes: d.detailNotes.trim() } : {}),
          };
        })
      : Object.entries(taskDrafts)
          .filter(([, d]) => d.checked)
          .map(([id, d]) => ({
            taskTypeId: Number(id),
            flag: true,
            ...(d.detailNotes.trim() ? { detailNotes: d.detailNotes.trim() } : {}),
          }));

    mutation.mutate({
      jobNumber: jobNumber.trim(),
      ...(jobName.trim()        ? { jobName:        jobName.trim() }        : {}),
      ...(logDate               ? { logDate }                                : {}),
      ...(title.trim()          ? { title:          title.trim() }          : {}),
      ...(notes.trim()          ? { notes:          notes.trim() }          : {}),
      ...(photoFolderUrl.trim() ? { photoFolderUrl: photoFolderUrl.trim() } : {}),
      ...(prefill?.surveyRequestId != null ? { surveyRequestId: prefill.surveyRequestId } : {}),
      ...(tasks.length          ? { tasks }                                  : {}),
    });
  };

  const inp: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', padding: '6px 10px', fontSize: 13, fontFamily: 'inherit', background: '#fff', color: 'var(--text)', width: '100%' };
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4, display: 'block' };

  const checkedCount = Object.values(taskDrafts).filter(d => d.checked).length;
  const jobFieldsLocked = !!prefill;
  const headerText = isEdit
    ? `Edit Visit Log #${editLog!.id}`
    : prefill
      ? `Log a Visit · #${prefill.jobNumber}${prefill.jobName ? ` — ${prefill.jobName}` : ''}`
      : 'Log a Visit';
  const saveLabel = mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Save log';

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 100,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 16px',
      }}
    >
      <div
        ref={modalRef}
        onClick={e => e.stopPropagation()}
        style={{
          background: '#f9fafb', borderRadius: 'var(--ew-r-lg)', width: 'min(960px, 100%)', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', background: '#fff', borderRadius: 'var(--ew-r-lg) var(--ew-r-lg) 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{headerText}</div>
          <button onClick={onClose}
            style={{ fontSize: 12, padding: '5px 12px', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', background: '#fff', cursor: 'pointer', color: '#6b7280' }}>
            Close
          </button>
        </div>

        <div style={{ padding: '16px 20px', overflowY: 'auto' }}>
          <DetailSection title="Visit Info">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
              <div>
                <label style={lbl}>Job # *</label>
                <input
                  style={{ ...inp, background: jobFieldsLocked ? '#f3f4f6' : '#fff' }}
                  value={jobNumber}
                  onChange={e => setJobNumber(e.target.value)}
                  placeholder="e.g. 10834"
                  readOnly={jobFieldsLocked}
                />
              </div>
              <div>
                <label style={lbl}>Job Name</label>
                <input
                  style={{ ...inp, background: jobFieldsLocked ? '#f3f4f6' : '#fff' }}
                  value={jobName}
                  onChange={e => setJobName(e.target.value)}
                  placeholder="Optional"
                  readOnly={jobFieldsLocked}
                />
              </div>
              <div>
                <label style={lbl}>Log Date *</label>
                <input style={inp} type="date" value={logDate} onChange={e => setLogDate(e.target.value)} />
              </div>
              <div>
                <label style={lbl}>Title</label>
                <input style={inp} value={title} onChange={e => setTitle(e.target.value)} placeholder="Optional short label" />
              </div>
            </div>
          </DetailSection>

          <div style={{ marginTop: 12 }}>
            <DetailSection title={`Tasks Performed${checkedCount ? ` (${checkedCount})` : ''}`}>
              {taskTypesLoading && <div style={{ fontSize: 12, color: '#9ca3af' }}>Loading task types…</div>}
              {taskTypesError && <div style={{ fontSize: 12, color: '#8F2414' }}>{(taskTypesError as Error).message}</div>}
              {!taskTypesLoading && taskTypes.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {taskTypes.map(tt => {
                    const draft = taskDrafts[tt.id] ?? { checked: false, detailNotes: '' };
                    return (
                      <div key={tt.id} style={{ border: '1px solid #f3f4f6', borderRadius: 'var(--ew-r-md)', padding: '8px 10px', background: draft.checked ? '#f0fdf4' : '#fff' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={draft.checked}
                            onChange={e => setTask(tt.id, { checked: e.target.checked })}
                          />
                          <span style={{ fontSize: 13, fontWeight: 600 }}>{tt.label}</span>
                        </label>
                        {draft.checked && (
                          <textarea
                            style={{ ...inp, resize: 'vertical', minHeight: 48, marginTop: 6 }}
                            value={draft.detailNotes}
                            onChange={e => setTask(tt.id, { detailNotes: e.target.value })}
                            placeholder={`Details about the ${tt.label.toLowerCase()} work performed (optional)`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </DetailSection>
          </div>

          <div style={{ marginTop: 12 }}>
            <DetailSection title="Notes & Photos">
              <label style={lbl}>Notes</label>
              <textarea
                style={{ ...inp, resize: 'vertical', minHeight: 70, marginBottom: 10 }}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="What was done, conditions on site, anything worth noting"
              />
              <label style={lbl}>Photo folder URL</label>
              <input style={inp} value={photoFolderUrl} onChange={e => setPhotoFolderUrl(e.target.value)} placeholder="e.g. SharePoint or OneDrive folder link" />
            </DetailSection>
          </div>

          {mutation.isError && (
            <div style={{ marginTop: 12, fontSize: 12, color: '#8F2414', background: '#FBE4DF', padding: 8, borderRadius: 'var(--ew-r-md)' }}>
              {(mutation.error as Error).message}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', background: '#fff', borderRadius: '0 0 var(--ew-r-lg) var(--ew-r-lg)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} disabled={mutation.isPending}
            style={{ fontSize: 12, padding: '6px 14px', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', background: '#fff', cursor: 'pointer', color: '#6b7280', fontWeight: 500 }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={!jobNumber.trim() || mutation.isPending}
            style={{
              fontSize: 12, padding: '6px 16px', border: 'none', borderRadius: 'var(--ew-r-md)', cursor: jobNumber.trim() ? 'pointer' : 'not-allowed', fontWeight: 600,
              background: jobNumber.trim() ? 'var(--ew)' : '#d1d5db', color: '#fff',
            }}>
            {saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Logs List ────────────────────────────────────────────────────────────────

function LogsList() {
  // POST /survey/logs requires survey.edit. Every other visit-log write on this
  // page already checks it (the drawer's + Add log at :460, Edit/Delete at
  // :746/:750) — this list was the one that didn't, so "+ Log a visit" rendered
  // for every signed-in user and 403'd after they'd filled the whole form.
  const canEdit = useCanEditSurvey();
  const [search, setSearch]       = useState('');
  const [from, setFrom]           = useState<string>('');
  const [to, setTo]               = useState<string>('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingLog, setEditingLog] = useState<SurveyLog | null>(null);

  const filters = useMemo(() => ({
    ...(search ? { job: search } : {}),
    ...(from   ? { from }   : {}),
    ...(to     ? { to }     : {}),
  }), [search, from, to]);

  const { data: logs = [], isLoading, error } = useQuery<SurveyLog[]>({
    queryKey: ['survey-logs', filters],
    queryFn: () => surveyApi.listLogs(filters),
  });

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8 }}>
        <span style={{ fontSize: 13, color: 'var(--gray)' }}>
          {isLoading ? 'Loading…' : `${logs.length} log${logs.length !== 1 ? 's' : ''}`}
        </span>
        {canEdit && (
          <button
            onClick={() => setShowCreate(true)}
            style={{ fontSize: 12, padding: '6px 14px', background: 'var(--ew)', color: '#fff', border: 'none', borderRadius: 'var(--ew-r-md)', cursor: 'pointer', fontWeight: 600 }}>
            + Log a visit
          </button>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 16, padding: '12px 16px', background: '#fff', borderRadius: 'var(--ew-r-lg)', border: '1px solid var(--border)' }}>
        <input
          type="search"
          placeholder="Search job number or job name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...selectStyle, minWidth: 260, flexGrow: 1 }}
        />
        <label style={{ fontSize: 12, color: 'var(--gray)' }}>
          From{' '}
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ ...selectStyle, padding: '5px 8px' }} />
        </label>
        <label style={{ fontSize: 12, color: 'var(--gray)' }}>
          To{' '}
          <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ ...selectStyle, padding: '5px 8px' }} />
        </label>
        {(search || from || to) && (
          <button
            onClick={() => { setSearch(''); setFrom(''); setTo(''); }}
            style={{ fontSize: 12, padding: '5px 12px', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', background: '#fff', cursor: 'pointer', color: '#6b7280' }}
          >
            Clear
          </button>
        )}
      </div>

      {error && (
        <div style={{ padding: 12, background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', marginBottom: 16, fontSize: 13 }}>
          {(error as Error).message}
        </div>
      )}

      <div style={{ background: '#fff', borderRadius: 'var(--ew-r-lg)', border: '1px solid var(--border)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '12%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '22%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '23%' }} />
          </colgroup>
          <thead>
            <tr>
              {['Log Date', 'Job #', 'Job Name', 'Logged By', 'Tasks', 'Linked Req.', 'Notes'].map(h => (
                <th key={h} style={{
                  padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 12,
                  color: 'var(--gray)', borderBottom: '2px solid var(--border)', background: 'var(--surface)',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={7} style={{ padding: 32, textAlign: 'center', color: 'var(--gray)' }}>Loading…</td></tr>
            )}
            {!isLoading && logs.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 32, textAlign: 'center', color: 'var(--gray)' }}>No visit logs match your filters.</td></tr>
            )}
            {logs.map(log => (
              <tr
                key={log.id}
                onClick={() => setSelectedId(log.id)}
                style={{ cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <td style={{ padding: '10px', fontWeight: 600 }}>{fmtDateOnly(log.logDate)}</td>
                <td style={{ padding: '10px', color: log.jobNumber ? '#1f2937' : '#9ca3af' }}>
                  {log.jobNumber ?? '—'}
                </td>
                <td style={{ padding: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: log.jobName ? '#1f2937' : '#9ca3af' }}>
                  {log.jobName ?? '—'}
                </td>
                <td style={{ padding: '10px', color: (log.loggedBy || log.loggedByText) ? '#1f2937' : '#9ca3af' }}>
                  {log.loggedBy?.displayName ?? log.loggedByText ?? '—'}
                </td>
                <td style={{ padding: '10px', color: log.tasks.length ? '#1f2937' : '#9ca3af' }}>
                  {log.tasks.length || '—'}
                </td>
                <td style={{ padding: '10px', color: log.surveyRequestId != null ? '#1f2937' : '#9ca3af' }}>
                  {log.surveyRequestId != null ? `#${log.surveyRequestId}` : '—'}
                </td>
                <td style={{ padding: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: log.notes ? '#1f2937' : '#9ca3af' }}>
                  {log.notes ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedId != null && (
        <LogDetailModal
          logId={selectedId}
          onClose={() => setSelectedId(null)}
          onEdit={(log) => { setEditingLog(log); setSelectedId(null); }}
        />
      )}
      {showCreate && (
        <LogCreateModal onClose={() => setShowCreate(false)} />
      )}
      {editingLog && (
        <LogCreateModal editLog={editingLog} onClose={() => setEditingLog(null)} />
      )}
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

type SurveyTab = 'requests' | 'logs';

export default function SurveyPage() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<SurveyTab>('requests');

  // If a request param appears (e.g. internal navigation from the Manpower
  // survey chip while the user is currently on the Logs tab), snap back to
  // Requests so the request detail modal can open.
  useEffect(() => {
    if (searchParams.has('request')) setTab('requests');
  }, [searchParams]);

  const tabBtnStyle = (key: SurveyTab): React.CSSProperties => ({
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 600,
    background: 'transparent',
    color: tab === key ? 'var(--ew)' : 'var(--gray)',
    border: 'none',
    borderBottom: tab === key ? '2px solid var(--ew)' : '2px solid transparent',
    cursor: 'pointer',
    marginBottom: -1,
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 12, gap: 12 }}>
        <h2 style={{ fontWeight: 700, fontSize: 18 }}>
          {tab === 'requests' ? 'Survey Requests' : 'Visit Logs'}
        </h2>
        {/* Opens the Formstack intake form in a new tab. Submissions flow back
            into this list via the survey-request webhook. */}
        <a
          href="https://redeapp.formstack.com/forms/survey_services_request_form_copy_1"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: 12, padding: '6px 14px',
            background: 'var(--ew)', color: '#fff',
            border: 'none', borderRadius: 'var(--ew-r-md)',
            cursor: 'pointer', fontWeight: 600,
            textDecoration: 'none', whiteSpace: 'nowrap',
          }}
        >
          + Survey Services Request
        </a>
      </div>

      {/* Sub-tab toggle */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', marginBottom: 16 }}>
        <button style={tabBtnStyle('requests')} onClick={() => setTab('requests')}>Requests</button>
        <button style={tabBtnStyle('logs')} onClick={() => setTab('logs')}>Logs</button>
      </div>

      {tab === 'requests' ? <RequestsList /> : <LogsList />}
    </div>
  );
}
